/**
 * hooks/useCaseActions.ts
 *
 * Mutations for client_cases: create, rename, delete, attach request to case.
 * Updated Apr 2026 — attachRequestToCase verifies that the target case and the
 *   request share the same client_id before updating, preventing cross-client attachment.
 */

import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface UseCaseActionsResult {
  loading: boolean;
  createCase: (clientId: string, name: string) => Promise<string | null>;
  renameCase: (caseId: string, name: string) => Promise<boolean>;
  deleteCase: (caseId: string) => Promise<boolean>;
  attachRequestToCase: (requestId: string, caseId: string) => Promise<boolean>;
}

export function useCaseActions(): UseCaseActionsResult {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const createCase = async (clientId: string, name: string): Promise<string | null> => {
    if (!user) return null;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('client_cases')
        .insert({ client_id: clientId, user_id: user.id, name })
        .select('id')
        .single();
      if (error) throw error;
      return data.id;
    } catch (err) {
      console.error('[useCaseActions] createCase error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const renameCase = async (caseId: string, name: string): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('client_cases')
        .update({ name, updated_at: new Date().toISOString() })
        .eq('id', caseId);
      if (error) throw error;
      return true;
    } catch (err) {
      console.error('[useCaseActions] renameCase error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteCase = async (caseId: string): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('client_cases')
        .delete()
        .eq('id', caseId);
      if (error) throw error;
      return true;
    } catch (err) {
      console.error('[useCaseActions] deleteCase error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const attachRequestToCase = async (requestId: string, caseId: string): Promise<boolean> => {
    setLoading(true);
    try {
      // Fetch the client_id of the target case to prevent cross-client attachment
      const { data: caseData, error: caseError } = await supabase
        .from('client_cases')
        .select('client_id')
        .eq('id', caseId)
        .maybeSingle();
      if (caseError) throw caseError;
      if (!caseData) throw new Error('Case not found');

      const { data: updated, error } = await supabase
        .from('requests')
        .update({ case_id: caseId })
        .eq('id', requestId)
        .eq('client_id', caseData.client_id)
        .select('id');
      if (error) throw error;
      if (!updated || updated.length === 0) throw new Error('Request not found or client_id mismatch');
      return true;
    } catch (err) {
      console.error('[useCaseActions] attachRequestToCase error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { loading, createCase, renameCase, deleteCase, attachRequestToCase };
}
